import React, { useEffect, useRef, useState } from "react";
import Checkbox from "../../../../Checkbox";

const ScreeningQuestionsTable = ({
  displayedData,
  loading,
  isAllChecked,
  handleSelectAll,
  selectedReqs,
  handleCheckboxChange,
  searchQuery,
  handleViewSource,
  showAnswerColumns,
}) => {
  const tbodyRef = useRef(null);

  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const savedWidths = localStorage.getItem("screeningTable_columnWidths");
      return savedWidths ? JSON.parse(savedWidths) : {};
    } catch (error) {
      return {};
    }
  });

  const handleResize = (colName, e) => {
    e.preventDefault();
    e.stopPropagation();

    const thElement = e.target.closest("th");
    const startWidth = thElement ? thElement.getBoundingClientRect().width : 150;
    const startX = e.clientX;

    document.body.style.userSelect = "none";

    const onMouseMove = (moveEvent) => {
      const newWidth = Math.max(100, startWidth + (moveEvent.clientX - startX));
      setColumnWidths((prev) => ({ ...prev, [colName]: newWidth }));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "default";
      document.body.style.userSelect = "";

      setColumnWidths((latestWidths) => {
        localStorage.setItem("screeningTable_columnWidths", JSON.stringify(latestWidths));
        return latestWidths;
      });
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
  };

  const getColStyle = (colName) => {
    return columnWidths[colName]
      ? {
          width: columnWidths[colName],
          minWidth: columnWidths[colName],
          maxWidth: columnWidths[colName],
        }
      : {};
  };

  return (
    <div className="w-full flex flex-col border border-[#0000001A] rounded-b-lg h-full">
      <div
        className="relative overflow-auto custom-scrollbar flex-1 rounded-b-lg bg-white"
        ref={tbodyRef}
      >
        <table className="w-full table-fixed text-left border-separate border-spacing-0">
          <thead className="h-[36px] sticky top-0 z-10">
            <tr className="bg-white">
              <th
                style={getColStyle("question")}
                className={`relative px-4 text-[#00000080] bg-[#F7F7F7] font-outfit text-[14px] font-semibold border-b border-r border-[#0000001A] ${
                  !columnWidths.question ? "w-[40%]" : ""
                }`}
              >
                Question
                <div
                  onMouseDown={(e) => handleResize("question", e)}
                  className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#A259FF] transition-colors z-10"
                />
              </th>

              <th
                style={getColStyle("rationale")}
                className={`relative px-4 text-[#00000080] bg-[#F7F7F7] font-outfit text-[14px] font-semibold border-b border-[#0000001A] ${
                  !columnWidths.rationale ? "w-[60%]" : ""
                }`}
              >
                AI Rationale
                <div
                  onMouseDown={(e) => handleResize("rationale", e)}
                  className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#A259FF] transition-colors z-10"
                />
              </th>
            </tr>
          </thead>

          <tbody className="bg-white">
            {loading ? (
              <tr>
                <td
                  colSpan={2}
                  className="py-24 text-center align-middle border-b border-[#0000001A]"
                >
                  <div className="flex flex-col items-center justify-center w-full h-full">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#BB42AE] mx-auto mb-4"></div>
                    <p className="text-[#52525B] font-outfit text-[14px]">
                      Fetching screening questions...
                    </p>
                  </div>
                </td>
              </tr>
            ) : displayedData.length > 0 ? (
              displayedData.map((item, index) => (
                <tr
                  key={item.id || index}
                  className="group hover:bg-[#F8F3FF] transition-colors cursor-default"
                >
                  <td
                    style={getColStyle("question")}
                    className={`px-4 py-3 border-b border-r border-[#0000001A] align-top ${
                      !columnWidths.question ? "w-[40%]" : ""
                    }`}
                  >
                    <span className="text-[#52525B] font-outfit text-[14px] font-medium leading-snug block">
                      {item.text || "N/A"}
                    </span>
                  </td>

                  <td
                    style={getColStyle("rationale")}
                    className={`px-4 py-3 border-b border-[#0000001A] align-top ${
                      !columnWidths.rationale ? "w-[60%]" : ""
                    }`}
                  >
                    {!showAnswerColumns ? (
                      <span className="text-[#9CA3AF] font-outfit text-[13px] italic block">
                        Generate answers to view AI rationale
                      </span>
                    ) : (
                      <div className="text-[#52525B] font-outfit text-[14px] leading-snug">
                        <span>
                          {item.answer || (
                            <span className="text-gray-400 italic">No rationale generated yet.</span>
                          )}
                        </span>
                        
                        {item.sources && item.sources.length > 0 && (
                          <button
                            onClick={(e) => handleViewSource(item, e)}
                            className="inline-block ml-2 text-[#A04A97] hover:text-[#7A4982] transition-colors font-outfit text-[13px] font-medium underline underline-offset-2 bg-transparent border-none cursor-pointer p-0"
                          >
                            View Source
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={2}
                  className="py-20 text-center border-b border-[#0000001A]"
                >
                  <img
                    src="/no-contracts.svg"
                    alt="No Data"
                    className="mx-auto mb-4 w-32"
                  />
                  <p className="text-[15px] font-outfit text-gray-500">
                    {searchQuery
                      ? `No results found for "${searchQuery}"`
                      : "No screening questions found."}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScreeningQuestionsTable;